import {GlobalConfig} from 'shared/global-config';
import {Injectable, Signal, effect, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Stammdaten} from '../dtos/stammdaten';
import {Stammdatum} from '../dtos/stammdatum';
import {TesmostaDatalistValidatorValue} from '../EnumsConstants/tesmosta-datalist-validator-value.enum';
import {DataService} from './data.service';
import {MessageService} from './message.service';

@Injectable({
  providedIn: 'root'
})
export class StammdatenService {
  public data: BehaviorSubject<Stammdaten> = new BehaviorSubject<Stammdaten>(
    new Stammdaten()
  );

  public readonly isLoading: Signal<boolean>;
  public readonly isInitialized: Signal<boolean>;
  public readonly stammdatenData: Signal<Stammdaten>;

  private readonly grundgesammheitLabels = [
    'm1-m2.spring10.grundgesamtheit.label.regulaer',
    'm1-m2.spring10.grundgesamtheit.label.berufslehre',
    'm1-m2.spring10.grundgesamtheit.label.praktikum',
    'm1-m2.spring10.grundgesamtheit.label.expat',
    'm1-m2.spring10.grundgesamtheit.label.andere'
  ];

  private readonly statusLabels = [
    'i18n.common.ungueltiger',
    'i18n.common.ausgeschlossen',
    'i18n.common.gueltiger'
  ];

  private readonly _stammdatenLoading = signal(false);
  private readonly _stammdatenInitialized = signal(false);
  private readonly _stammdatenSignal = signal<Stammdaten>(new Stammdaten());

  constructor(
    private readonly translateService: TranslateService,
    private readonly dataService: DataService,
    private readonly messageService: MessageService
  ) {
    this.isLoading = this._stammdatenLoading.asReadonly();
    this.isInitialized = this._stammdatenInitialized.asReadonly();
    this.stammdatenData = this._stammdatenSignal.asReadonly();

    this.messageService
      .getListOfFunctionsChanged()
      .subscribe(() => this.populateStammdaten());
    effect(() => {
      this.data.next(this._stammdatenSignal());
    });
  }

  initialize() {
    this.initializeStammdaten();
  }

  getStamdatenByKey(key: string): Observable<Stammdatum[]> {
    return this.data.pipe(map(data => data[key]));
  }

  async populateStammdaten() {
    if (this._stammdatenLoading()) {
      return;
    }

    this._stammdatenLoading.set(true);

    try {
      const newStammdaten = {
        status: this.getStatusData(),
        geschlecht: [new Stammdatum('M', 'M'), new Stammdatum('F', 'F')],
        grundgesamtheit: this.getGrundgesamtheitData(),
        ausbildung: this.generateData(1, 8),
        beruflicheStellung: this.generateData(1, 5),
        beruflichesKompetenzniveau: this.generateData(1, GlobalConfig.anfMax),
        buwaz: this.generateData(1, 100),
        funktionen: await this.dataService.getSortedFunctionen()
      };

      this._stammdatenSignal.set(newStammdaten);
      this._stammdatenInitialized.set(true);
    } finally {
      this._stammdatenLoading.set(false);
    }
  }

  updateStammdatenLabels() {
    if (!this._stammdatenInitialized()) {
      this.initializeStammdaten();
      return;
    }

    const currentData = this._stammdatenSignal();
    this.setLabels(currentData.grundgesamtheit, this.grundgesammheitLabels);
    this.setLabels(currentData.status, this.statusLabels);
    this._stammdatenSignal.set({...currentData});
  }

  private async initializeStammdaten() {
    if (!this._stammdatenInitialized() && !this._stammdatenLoading()) {
      await this.populateStammdaten();
    }
  }

  private getGrundgesamtheitData() {
    const stmDt = [];
    for (let idx = 0; idx < this.grundgesammheitLabels.length; idx++) {
      const label = this.translateService.instant(
        this.grundgesammheitLabels[idx]
      );
      const value = (idx + 1).toString();
      const data = new Stammdatum(value, label);
      stmDt.push(data);
    }
    return stmDt;
  }

  private getStatusData() {
    const stmDt = [];
    const value1 = TesmostaDatalistValidatorValue.UngueltigerWert;
    const caption1 = this.translateService.instant('i18n.common.ungueltiger');
    const value2 = TesmostaDatalistValidatorValue.AuswertungAusgeschlossen;
    const caption2 = this.translateService.instant(
      'i18n.common.ausgeschlossen'
    );
    const value3 = TesmostaDatalistValidatorValue.KorrektWert;
    const caption3 = this.translateService.instant('i18n.common.gueltiger');

    stmDt.push(new Stammdatum(value1, caption1));
    stmDt.push(new Stammdatum(value2, caption2));
    stmDt.push(new Stammdatum(value3, caption3));

    return stmDt;
  }

  private setLabels(stamdatum: Stammdatum[], arrOfLabels: string[]) {
    stamdatum.forEach(
      (item: Stammdatum, idx: number) =>
        (item.caption = this.translateService.instant(arrOfLabels[idx]))
    );
  }

  private generateData(rangeMin: number, rangeMax: number) {
    const arr = [];
    for (let index = rangeMin; index < rangeMax + 1; index++) {
      const data = new Stammdatum(index, index.toString());
      arr.push(data);
    }
    return arr;
  }
}
